import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import { Avatar, Icon, Popover, ResourceItem, ResourceList, Spinner, Text } from "@shopify/polaris";
import { NotificationMajor } from "@shopify/polaris-icons";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ContextUser } from "src/context/ContextUser";
import { Graph } from "src/generated/graph";
import { Toast } from "src/hooks/toast";

const QUERY = gql`
  query sellerNotificationList($offset: Int = 0, $limit: Int = 20) {
    sellerNotificationList(offset: $offset, limit: $limit) {
      attachedImage
      createdAt
      data
      id
      notificationType
      read
      sellerId
      subTitle
      title
    }
  }
`;

const MUTATION = gql`
  mutation markSellerNotificationSeen($markSellerNotificationSeenId: Int!) {
    markSellerNotificationSeen(id: $markSellerNotificationSeenId)
  }
`;

const SUBSCRIPTION = gql`
  subscription supplierEventAdded($type: String, $supplierEventAddedId: Int) {
    supplierEventAdded(type: $type, id: $supplierEventAddedId) {
      message
      payload
      supplierId
      type
    }
  }
`;

function notifyMe(text: string) {
  const img = "/favicon.png";
  if (!("Notification" in window)) {
    alert("This browser does not support desktop notification");
  } else if (Notification.permission === "granted") {
    const notification = new Notification("L192 Seller", { body: text, icon: img });
    setTimeout(() => {
      notification.close();
    }, 5000);
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        const notification = new Notification("L192 Seller", { body: text, icon: img });
        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    });
  }
}

export function NotificationMenuMarkup() {
  const text = "You have new notification.";
  const { merchant } = useContext(ContextUser);
  const [open, setOpen] = useState(false);
  const { data, loading, refetch } = useQuery(QUERY, {
    fetchPolicy: "no-cache",
  });
  const [markSellerNotificationSeen] = useMutation(MUTATION, {
    refetchQueries: ["sellerNotificationList"],
  });
  const { data: newNotification } = useSubscription<Graph.SupplierEventAdded>(SUBSCRIPTION, {
    variables: {
      type: "INFORMATION",
      supplierEventAddedId: Number(merchant.id),
    },
  });

  const toggleOpen = useCallback(() => setOpen(!open), [open]);

  useEffect(() => {
    if (newNotification) {
      if (newNotification.supplierId === merchant.id) {
        console.log("---------New notification---------");
        notifyMe(text);
        Toast.onShow(text, "info", "right");
        refetch({
          variables: {
            offset: 0,
            limit: 20,
          },
        });
      }
    }
  }, [newNotification, merchant, refetch]);

  return (
    <Popover
      activator={
        <div className="Polaris-TopBar-Menu__ActivatorWrapper">
          <div
            onClick={toggleOpen}
            style={{ display: "flex", position: "relative", fontSize: "14pt", alignItems: "center" }}
            className="Polaris-TopBar-Menu__Activator"
          >
            {loading ? (
              <Spinner accessibilityLabel="Small spinner example" size="small" />
            ) : (
              <Icon source={NotificationMajor as any} />
            )}
            {data && data.sellerNotificationList.filter((x: any) => !x.read).length > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -15,
                  zIndex: 100,
                  right: -10,
                }}
              >
                <span
                  style={{
                    padding: "2px 6px",
                    backgroundColor: "red",
                    color: "white",
                    borderRadius: "0.5rem",
                    fontSize: "10pt",
                  }}
                >
                  {data.sellerNotificationList.filter((x: any) => !x.read).length}
                </span>
              </span>
            )}
          </div>
        </div>
      }
      active={open}
      onClose={toggleOpen}
    >
      {data && (
        <Popover.Pane>
          {" "}
          <ResourceList
            items={data.sellerNotificationList}
            renderItem={(item: any) => {
              const media = (
                <Avatar
                  initials={String(item.notificationType).substring(0, 2)}
                  name={item.notificationType || ""}
                  source={item.attachedImage || ""}
                />
              );
              return (
                <ResourceItem
                  media={media}
                  id={item.id}
                  onClick={() => {
                    if (item.data.url) {
                      window.open(item.data.url, "_blank");
                    }
                    markSellerNotificationSeen({
                      variables: {
                        markSellerNotificationSeenId: Number(item.id),
                      },
                    });
                  }}
                >
                  <Text variant="bodySm" fontWeight="bold" as="h3">
                    {item.title}
                  </Text>
                  <small>{item.subTitle}</small>
                </ResourceItem>
              );
            }}
          />{" "}
        </Popover.Pane>
      )}
    </Popover>
  );
}
